<script lang="ts" module>
  /** The "How Scoring Works" primer rows. Exported for points-scoring.test.ts. */
  export const SCORING = [
    { pts: '+5', label: 'Vote on a track (in the app)' },
    { pts: '+7', label: 'Make a top pick (in the app)' },
    { pts: '+10', label: 'Submit a track (max 3 per duel)' },
  ];
</script>

<script lang="ts">
  import { untrack } from 'svelte';
  import { api } from '$shared/api';
  import { getCurrentRoute } from '$lib/router.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import { formatRelativeTime } from '$lib/utils/time';

  interface StatsResponse {
    points: number;
    rank: number;
    total_players: number;
  }

  interface SeasonStats {
    points: number;
    rank: number;
    season_number: number;
  }

  interface LedgerRow {
    type: string;
    action: string;
    points: number;
    occurred_at: string;
    context_label: string | null;
    track_id: number | null;
    duel_id: number | null;
    ref_id: number | null;
    detail_count: number | null;
  }

  interface LedgerResponse {
    items: LedgerRow[];
    page: number;
    per_page: number;
    has_more: boolean;
  }

  interface BreakdownItem {
    label: string;
    detail: string;
    points: number;
  }

  let stats = $state<StatsResponse | null>(null);
  let season = $state<SeasonStats | null>(null);
  // Active tab — URL-persisted (#/points?tab=all-time) for shareability; default This Week.
  let scope = $state<'season' | 'alltime'>(
    untrack(() => (new URLSearchParams(getCurrentRoute().query).get('tab') === 'all-time' ? 'alltime' : 'season')),
  );
  let rows = $state<LedgerRow[]>([]);
  let page = $state(1);
  let hasMore = $state(false);
  let loading = $state(true);
  let loadingMore = $state(false);

  let expanded = $state<Set<string>>(new Set());
  let breakdowns = $state<Record<string, BreakdownItem[]>>({});
  let loadingKey = $state<string | null>(null);

  function rowLink(r: LedgerRow): string | null {
    // Only duel-result rows link anywhere; game-ledger rows (ai_vs_you, beat_ai,
    // underground, detective, discovery) are web-display-only — those games are
    // iOS-only now, so there's no web route to send the row to.
    return r.duel_id ? `#/duels/${r.duel_id}/result` : null;
  }

  function rowKey(r: LedgerRow): string {
    return `${r.type}:${r.ref_id}:${r.occurred_at}`;
  }

  function isExpandable(r: LedgerRow): boolean {
    return (r.type === 'detective' || r.type === 'discovery') && r.ref_id != null;
  }

  async function toggleRow(r: LedgerRow): Promise<void> {
    const k = rowKey(r);
    if (expanded.has(k)) {
      expanded.delete(k);
      expanded = new Set(expanded);
      return;
    }
    expanded.add(k);
    expanded = new Set(expanded);
    if (!breakdowns[k]) {
      loadingKey = k;
      try {
        const res = await api.get<{ items: BreakdownItem[] }>(
          `/users/current/points-log/breakdown?type=${r.type}&id=${r.ref_id}`,
        );
        breakdowns = { ...breakdowns, [k]: res.items };
      } catch (e) {
        console.error('Failed to load breakdown:', e);
      } finally {
        loadingKey = null;
      }
    }
  }

  async function loadPage(p: number): Promise<void> {
    const apiScope = scope === 'season' ? 'this-season' : 'all-time';
    const res = await api.get<LedgerResponse>(`/users/current/points-log?page=${p}&scope=${apiScope}`);
    rows = p === 1 ? res.items : [...rows, ...res.items];
    hasMore = res.has_more;
    page = res.page;
  }

  async function loadMore(): Promise<void> {
    loadingMore = true;
    try {
      await loadPage(page + 1);
    } catch (e) {
      console.error('Failed to load more points:', e);
    } finally {
      loadingMore = false;
    }
  }

  async function setScope(next: 'season' | 'alltime'): Promise<void> {
    if (scope === next) return;
    scope = next;
    // URL-persist the tab without a router remount (shareable + back-friendly enough).
    history.replaceState(null, '', '#/points' + (next === 'alltime' ? '?tab=all-time' : ''));
    // Reset the activity ledger for the new scope.
    rows = [];
    page = 1;
    hasMore = false;
    expanded = new Set();
    breakdowns = {};
    loading = true;
    try {
      await loadPage(1);
    } catch (e) {
      console.error('Failed to load points:', e);
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    Promise.all([
      api.get<StatsResponse>('/users/current/stats').then((d) => {
        stats = d;
      }),
      api
        .get<SeasonStats>('/users/current')
        .then((d) => {
          season = d;
        })
        .catch(() => {
          season = null;
        }),
      loadPage(1),
    ])
      .catch((e) => console.error('Failed to load points:', e))
      .finally(() => {
        loading = false;
      });
  });
</script>

<div class="points-page">
  <h1>Points</h1>

  {#if loading}
    <LoadingSkeleton lines={6} />
  {:else}
    <div class="scope-toggle" role="tablist" aria-label="Points scope">
      <button
        class="scope-btn"
        class:scope-btn--active={scope === 'season'}
        role="tab"
        aria-selected={scope === 'season'}
        onclick={() => setScope('season')}
      >
        This Week
      </button>
      <button
        class="scope-btn"
        class:scope-btn--active={scope === 'alltime'}
        role="tab"
        aria-selected={scope === 'alltime'}
        onclick={() => setScope('alltime')}
      >
        All-Time
      </button>
    </div>

    {#if scope === 'season' && season}
      <div class="summary-group">
        <div class="summary-heading">This Week (W{season.season_number})</div>
        <div class="summary-cards">
          <div class="summary-card">
            <span class="summary-value season-points">{season.points.toLocaleString()}</span>
            <span class="summary-label">Points</span>
          </div>
          <a class="summary-card summary-card--link" href="#/leaderboard?season=current">
            <span class="summary-value season-rank">{season.rank > 0 ? `#${season.rank}` : 'Unranked'}</span>
            <span class="summary-label">Rank ›</span>
          </a>
        </div>
      </div>
    {:else if scope === 'alltime' && stats}
      <div class="summary-group">
        <div class="summary-heading">All-Time</div>
        <div class="summary-cards">
          <div class="summary-card">
            <span class="summary-value alltime-points">{stats.points.toLocaleString()}</span>
            <span class="summary-label">Points</span>
          </div>
          <a class="summary-card summary-card--link" href="#/leaderboard?period=all">
            <span class="summary-value alltime-rank">{stats.rank > 0 ? `#${stats.rank}` : 'Unranked'}</span>
            <span class="summary-label">of {stats.total_players} players ›</span>
          </a>
        </div>
      </div>
    {/if}

    {#if (stats?.points ?? 0) === 0}
      <!-- Onboarding primer: only shown to players who have never scored. Once
           they bank any all-time points it's just noise, so it disappears. -->
      <section class="scoring">
        <h2>How Scoring Works</h2>
        <ul class="scoring-list">
          {#each SCORING as s}
            <li>
              <span class="scoring-badge">{s.pts} pts</span>
              <span class="scoring-label">{s.label}</span>
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    <section class="ledger">
      <h2>Points Activity</h2>
      {#if rows.length === 0}
        <p class="empty">
          You haven't earned any points yet. Play games in the Beat Duel app or
          <a href="#/duels">join a duel</a> to get started.
        </p>
      {:else}
        <ul class="ledger-list">
          {#each rows as r (r.type + r.occurred_at + (r.duel_id ?? '') + (r.track_id ?? ''))}
            {@const link = rowLink(r)}
            {@const expandable = isExpandable(r)}
            {@const k = rowKey(r)}
            {@const isOpen = expanded.has(k)}
            <li class="ledger-item">
              {#if expandable}
                <button class="ledger-row ledger-row-expandable" onclick={() => toggleRow(r)} aria-expanded={isOpen}>
                  <div class="row-main">
                    <span class="row-action">{r.action}</span>
                    {#if r.context_label}
                      <span class="row-context">{r.context_label}</span>
                    {/if}
                  </div>
                  <div class="row-side">
                    <span class="row-points">+{r.points} pts</span>
                    <span class="row-time">{formatRelativeTime(r.occurred_at)}</span>
                  </div>
                  <span class="row-chevron" class:open={isOpen}>▸</span>
                </button>
              {:else}
                <div class="ledger-row">
                  <div class="row-main">
                    <span class="row-action">{r.action}</span>
                    {#if r.context_label}
                      {#if link}
                        <a class="row-context" href={link}>{r.context_label}</a>
                      {:else}
                        <span class="row-context">{r.context_label}</span>
                      {/if}
                    {/if}
                  </div>
                  <div class="row-side">
                    <span class="row-points">+{r.points} pts</span>
                    <span class="row-time">{formatRelativeTime(r.occurred_at)}</span>
                  </div>
                </div>
              {/if}
              {#if expandable && isOpen}
                <div class="breakdown-panel">
                  {#if loadingKey === k}
                    <p class="breakdown-loading">Loading…</p>
                  {:else if breakdowns[k]?.length}
                    <ul class="breakdown-list">
                      {#each breakdowns[k] as item}
                        <li class="breakdown-item">
                          <div class="breakdown-left">
                            <span class="breakdown-label">{item.label}</span>
                            {#if item.detail}
                              <span class="breakdown-detail">{item.detail}</span>
                            {/if}
                          </div>
                          <span class="breakdown-points">{item.points >= 0 ? '+' : ''}{item.points} pts</span>
                        </li>
                      {/each}
                    </ul>
                  {:else}
                    <p class="breakdown-loading">No details available.</p>
                  {/if}
                </div>
              {/if}
            </li>
          {/each}
        </ul>
        {#if hasMore}
          <button class="load-more" onclick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        {/if}
      {/if}
    </section>
  {/if}
</div>

<style>
  .points-page {
    max-width: 720px;
    margin: 0 auto;
  }
  h1 {
    margin-bottom: 1.5rem;
  }
  h2 {
    font-size: 1.1rem;
    margin: 2rem 0 0.75rem;
  }

  /* ── Dual summary ── */
  .scope-toggle {
    display: inline-flex;
    gap: 0.25rem;
    padding: 0.25rem;
    margin-bottom: 1rem;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--surface);
  }
  .scope-btn {
    padding: 0.4rem 1.1rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-secondary);
    background: none;
    border: none;
    border-radius: 999px;
    cursor: pointer;
  }
  .scope-btn--active {
    color: #fff;
    background: var(--pink);
  }
  .summary-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
  .summary-card--link {
    display: block;
    cursor: pointer;
    text-decoration: none;
    transition: border-color 0.15s;
  }
  .summary-card--link:hover {
    border-color: var(--pink);
  }
  .summary-heading {
    font-size: 0.8125rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-secondary);
  }
  .summary-cards {
    display: flex;
    gap: 1rem;
  }
  .summary-card {
    flex: 1;
    text-align: center;
    padding: 1.1rem 1rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .summary-value {
    display: block;
    font-size: 1.75rem;
    font-weight: 700;
  }
  .season-points {
    color: var(--cyan);
  }
  .season-rank {
    color: var(--pink);
  }
  .alltime-points {
    color: var(--cyan);
    opacity: 0.75;
  }
  .alltime-rank {
    color: var(--pink);
    opacity: 0.75;
  }
  .summary-label {
    font-size: 0.75rem;
    color: var(--text-secondary);
  }

  /* ── How Scoring Works ── */
  .scoring-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .scoring-list li {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .scoring-badge {
    flex: 0 0 auto;
    min-width: 64px;
    text-align: center;
    background: linear-gradient(135deg, var(--pink), var(--purple));
    color: #fff;
    font-weight: 700;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 0.8125rem;
  }
  .scoring-label {
    color: var(--text-secondary);
    font-size: 0.9375rem;
  }

  /* ── Ledger ── */
  .ledger-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .ledger-item {
    display: flex;
    flex-direction: column;
  }
  .ledger-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.85rem 1rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .ledger-row-expandable {
    width: 100%;
    cursor: pointer;
    text-align: left;
    color: inherit;
    font-family: inherit;
    font-size: inherit;
    transition: border-color 0.15s;
  }
  .ledger-row-expandable:hover {
    border-color: var(--cyan);
  }
  .row-main {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
  }
  .row-action {
    font-weight: 600;
    font-size: 0.9375rem;
  }
  .row-context {
    font-size: 0.8125rem;
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  a.row-context {
    color: var(--cyan);
  }
  .row-side {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.15rem;
  }
  .row-points {
    font-weight: 700;
    color: var(--cyan);
    font-size: 0.95rem;
  }
  .row-time {
    font-size: 0.75rem;
    color: var(--text-muted);
  }
  .row-chevron {
    flex: 0 0 auto;
    font-size: 0.75rem;
    color: var(--text-muted);
    transition: transform 0.15s;
    margin-left: 0.25rem;
  }
  .row-chevron.open {
    transform: rotate(90deg);
  }

  /* ── Breakdown panel ── */
  .breakdown-panel {
    background: var(--surface-alt, var(--surface));
    border: 1px solid var(--border);
    border-top: none;
    border-radius: 0 0 var(--radius) var(--radius);
    padding: 0.75rem 1rem;
  }
  .breakdown-loading {
    font-size: 0.875rem;
    color: var(--text-muted);
    margin: 0;
    padding: 0.25rem 0;
  }
  .breakdown-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .breakdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    font-size: 0.875rem;
  }
  .breakdown-left {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .breakdown-label {
    font-weight: 500;
    color: var(--text);
  }
  .breakdown-detail {
    font-size: 0.8125rem;
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .breakdown-points {
    flex: 0 0 auto;
    font-weight: 600;
    color: var(--cyan);
    font-size: 0.875rem;
  }

  .empty {
    color: var(--text-muted);
    font-size: 0.9375rem;
  }
  .empty a {
    color: var(--cyan);
  }
  .load-more {
    margin-top: 1rem;
    width: 100%;
    padding: 0.75rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    color: var(--text);
    font-weight: 600;
    cursor: pointer;
  }
  .load-more:disabled {
    opacity: 0.6;
    cursor: default;
  }

  @media (max-width: 480px) {
    .summary-cards {
      flex-direction: column;
    }
  }
</style>
